<template>
  <v-popover
    v-if="typeof saving === 'boolean'"
    placement="right"
    trigger="manual"
    offset="8"
    popover-class="input-saving--popover"
    :delay="{ hide: 1500 }"
    :open="saving"
    :auto-hide="false"
  >
    <input
      ref="amountInput"
      :value="internalValue"
      name="saving-input"
      class="ui-saving-input"
      type="text"
      placeholder="-"
      v-bind="$attrs"
      v-on="otherListeners"
      @focus="handleFocus"
      @beforeinput="handleBeforeInput"
      @input="handleInput"
      @blur="handleBlur"
      @paste="handlePaste"
    />
    <template #popover>
      <div class="tooltip--data">
        <v-progress-circular v-if="saving" color="blue500" indeterminate :size="10" :width="2" class="d-flex mr-2" />
        <v-icon size="10" v-else>mdi-check-circle</v-icon>
        {{ $t(`base.${saving ? 'saving' : 'saved'}`) }}
      </div>
    </template>
  </v-popover>
  <input
    v-else
    ref="amountInput"
    :value="internalValue"
    name="saving-input"
    class="ui-saving-input"
    type="text"
    placeholder="-"
    v-bind="$attrs"
    v-on="otherListeners"
    @focus="handleFocus"
    @beforeinput="handleBeforeInput"
    @input="handleInput"
    @blur="handleBlur"
    @paste="handlePaste"
  />
</template>

<script>
import debounce from 'lodash/debounce'
import omit from 'lodash/omit'
import { INPUT_DELAY_TIME } from '@/constants/variables'
import { numberWithSpaces } from '@/helpers/render-value'

export default {
  name: 'ui-saving-input',
  props: {
    value: null,
    saving: {
      type: Boolean,
      default: null,
    },
    integer: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    positive: {
      type: Boolean,
      default: false,
    },
    showZero: {
      type: Boolean,
      default: false,
    },
    returnValue: {
      type: [Number, String, Boolean],
      default: '',
    },
  },
  data() {
    return {
      internalValue: null,
      valueSaved: true,
      typing: false,
    }
  },
  computed: {
    otherListeners() {
      return omit(this.$listeners, ['input'])
    },
  },
  watch: {
    value: {
      handler(v) {
        if (this.typing) return

        if (v === undefined || v === null || v === '' || v === '-' || (Number(v) === 0 && !this.showZero)) {
          this.internalValue = ''
          return
        }
        let num = Number(v.toString().replace(/\s/g, ''))
        if (isNaN(num)) {
          this.internalValue = ''
          return
        }
        if (num % 1 !== 0) {
          this.internalValue = numberWithSpaces(num.toFixed(2))
        } else {
          this.internalValue = numberWithSpaces(String(num))
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.autofocus) this.$refs.amountInput.focus()
  },
  methods: {
    handleFocus() {
      this.typing = true
      if (this.internalValue === '' || this.internalValue === '-') {
        this.internalValue = ''
      }
    },
    handleBeforeInput(e) {
      if (e.inputType === 'insertFromPaste') return
      const char = e.data
      if (!char) return
      if (!this.positive) {
        if (char === '-') {
          if (this.internalValue.includes('-') || e.target.selectionStart !== 0) {
            e.preventDefault()
          }
          return
        }
      } else {
        if (char === '-') {
          e.preventDefault()
          return
        }
      }
      if (this.integer) {
        if (!/^[0-9]$/.test(char)) e.preventDefault()
        return
      }
      if (!/^[0-9.]$/.test(char)) {
        e.preventDefault()
        return
      }
      if (char === '.' && this.internalValue.includes('.')) {
        e.preventDefault()
      }
    },
    sanitizeValue(value) {
      if (typeof value !== 'string') return null
      let v = value
        .replace(/\s+/g, '')
        .replace(/\u00A0/g, '')
        .replace(',', '.')
      if (this.integer) {
        if (!/^-?\d+$/.test(v)) return null
        const num = Number(v)
        if (isNaN(num)) return null
        return this.positive ? Math.abs(num) : num
      }
      if (!/^-?\d+(\.\d+)?$/.test(v)) return null
      let num = Number(v)
      if (isNaN(num)) return null
      if (this.positive) num = Math.abs(num)
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },
    handlePaste(e) {
      e.preventDefault()
      const pastedRaw = e.clipboardData.getData('text')
      if (!pastedRaw) return
      const input = e.target
      const start = input.selectionStart
      const end = input.selectionEnd
      const current = String(this.internalValue ?? '')
      const nextValue = current.slice(0, start) + pastedRaw + current.slice(end)
      const sanitized = this.sanitizeValue(nextValue)
      if (sanitized === null) return
      this.internalValue = sanitized
      this.$emit('input', this.internalValue)
      this.valueSaved = false
      this.debouncedSave()
      this.$nextTick(() => {
        const pos = start + pastedRaw.length
        input.setSelectionRange(pos, pos)
      })
    },
    handleInput(e) {
      const input = e.target
      const cursor = input.selectionStart
      const nonSpacesBefore = input.value.substring(0, cursor).replace(/\s/g, '').length

      const plain = input.value.replace(/\s/g, '')
      let formatted = ''

      if (plain === '' || plain === '-') {
        formatted = plain
      } else {
        const num = Number(plain)
        if (isNaN(num)) {
          formatted = plain
        } else {
          if (plain.includes('.')) {
            const [int, dec] = plain.split('.')
            formatted = numberWithSpaces(int) + '.' + (dec || '')
          } else {
            formatted = numberWithSpaces(plain)
          }
        }
      }

      this.internalValue = formatted
      this.$emit('input', plain)
      this.valueSaved = false
      this.debouncedSave()

      this.$nextTick(() => {
        if (!input) return
        let newCursor = 0
        let foundNonSpaces = 0
        while (newCursor < formatted.length && foundNonSpaces < nonSpacesBefore) {
          if (formatted[newCursor] !== ' ') {
            foundNonSpaces++
          }
          newCursor++
        }
        input.setSelectionRange(newCursor, newCursor)
      })
    },
    handleBlur() {
      this.typing = false
      if (this.internalValue === '') {
        this.internalValue = this.returnValue || null
        this.saveOnBlur()
        return
      }
      if (this.integer) {
        this.internalValue = numberWithSpaces(Number(this.internalValue.toString().replace(/\s/g, '')))
        this.saveOnBlur()
        return
      }
      if (typeof this.internalValue === 'string') {
        let [i, d] = this.internalValue.toString().replace(/\s/g, '').split('.')
        if (!d) {
          this.internalValue = numberWithSpaces(Number(i))
        } else {
          this.internalValue = numberWithSpaces(Number(this.internalValue.toString().replace(/\s/g, '')).toFixed(2))
        }
      }
      this.saveOnBlur()
    },
    saveOnBlur() {
      const resString = this.internalValue ? this.internalValue.toString().replace(/\s/g, '') : ''
      this.$emit('input', resString)
      if (!this.valueSaved) {
        this.$emit('inputDelay', resString)
        this.valueSaved = true
      }
    },
    markSaved() {
      this.valueSaved = true
    },
    debouncedSave: debounce(function () {
      if (this.valueSaved || !this.internalValue) return
      this.$emit('inputDelay', this.internalValue.toString().replace(/\s/g, ''))
      this.valueSaved = true
    }, INPUT_DELAY_TIME),
  },
}
</script>

<style lang="scss" scoped>
.ui-saving-input {
  background-color: white;
  width: 100%;
  height: 30px;
  border: 1px solid var(--v-black-lighten6);
  border-radius: 6px;
  padding: 0 11px;
  font-size: 12px;
  color: var(--v-black-darken3);
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }
  &[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }
  &:focus::placeholder {
    color: transparent;
  }
  &::placeholder {
    color: var(--v-black-darken3);
  }
  &:focus-visible {
    outline: none !important;
    border: 2px solid var(--v-primary-base) !important;
    padding: 0 10px;
  }
}
</style>

<style lang="scss">
.tooltip.input-saving--popover {
  &[x-placement^='right'] {
    padding-left: 0;
  }
  .wrapper {
    .tooltip-inner {
      padding: 4px !important;
    }
    .tooltip-arrow {
      display: none;
    }
  }
}
</style>
